import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import {
    LayoutDashboard, Package, BarChart3, Users, Shield, Settings,
    LogOut, Building2, Zap, Clock, ChevronsLeft, ChevronsRight, X, ShoppingCart, Layers, FileText, DollarSign, Truck, Ship, Plug, Microscope,
} from 'lucide-react';

export const Sidebar = () => {
    const { user, company, modules, logout, isAdmin, isSuperAdmin } = useAuth();
    const { collapsed, toggle, mobileOpen, closeMobile } = useSidebar();
    const location = useLocation();

    const allMenuItems = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', section: null, page: 'dashboard', module: null },
        { to: '/fast-stock', icon: Zap, label: 'Hizli Stok', section: null, highlight: true, page: 'fast-stock', module: 'stock' },
        { to: '/sales?new=1', icon: Zap, label: 'Hizli Siparis', section: null, highlight: true, page: 'sales', module: 'sales' },
        { to: '/stock', icon: BarChart3, label: 'Stok', section: 'ENVANTER', page: 'stock', module: 'stock' },
        { to: '/raw-materials', icon: Layers, label: 'Hammadde', section: null, page: 'raw-materials', module: 'raw_materials' },
        { to: '/products', icon: Package, label: 'Urunler', section: 'KATALOG', page: 'products', module: 'stock' },
        { to: '/documents', icon: FileText, label: 'Dokumanlar', section: null, page: 'documents', module: 'documents' },
        { to: '/sales', icon: ShoppingCart, label: 'Satis', section: 'SATIS', page: 'sales', module: 'sales' },
        { to: '/ecommerce', icon: Package, label: 'E-Ticaret', section: null, page: 'ecommerce', module: 'sales' },
        { to: '/transport', icon: Truck, label: 'Transport', section: null, page: 'transport', module: 'sales' },
        { to: '/logistics', icon: Ship, label: 'Lojistik', section: null, page: 'logistics', module: 'sales' },
        { to: '/research', icon: Microscope, label: 'Arastirma', section: null, page: 'research', module: 'sales', newTab: true },
        { to: '/accounting', icon: DollarSign, label: 'Muhasebe', section: null, page: 'accounting', module: 'accounting' },
        { to: '/reports', icon: BarChart3, label: 'Raporlar', section: null, page: 'reports', module: 'reports' },
        { to: '/hr', icon: Clock, label: 'HR', section: null, page: 'hr', module: 'hr' },
        { to: '/users', icon: Users, label: 'Kullanicilar', section: 'AYARLAR', page: 'users', module: null },
        { to: '/roles', icon: Shield, label: 'Roller', section: null, page: 'roles', module: null },
        { to: '/integrations', icon: Plug, label: 'Entegrasyonlar', section: null, page: 'integrations', module: null },
        { to: '/settings', icon: Settings, label: 'Ayarlar', section: null, page: 'settings', module: null },
    ];

    // Filter by active modules
    const moduleFilteredItems = allMenuItems.filter(item => {
        if (!item.module) return true; // core items always visible
        if (!modules) return true; // no module config yet = show all
        return modules[item.module] !== false;
    });

    const menuItems = isSuperAdmin ? [
        { to: '/superadmin', icon: Building2, label: 'All Companies' }
    ] : isAdmin ? moduleFilteredItems : moduleFilteredItems.filter(item => {
        const userPages = user?.permissions?.pages || [];
        // No role or no pages defined = show default pages
        if (!user?.role_id || userPages.length === 0) {
            return ['dashboard', 'stock', 'products', 'fast-stock', 'sales'].includes(item.page);
        }
        if (item.page === 'dashboard') return true;
        return userPages.includes(item.page);
    });

    const sidebarContent = (isMobile = false) => (
        <>
            <div className={`sidebar-header ${collapsed && !isMobile ? 'sidebar-header-collapsed' : ''}`}>
                {(!collapsed || isMobile) ? (
                    <>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <img src="/flowbil_512.png" alt="Flowbil" className="w-7 h-7 rounded-lg" />
                                <div className="sidebar-logo">{company?.name || 'Flowbil'}</div>
                            </div>
                            <div className="flex items-center gap-1">
                                {isMobile ? (
                                    <button onClick={closeMobile} className="btn-icon" data-testid="close-mobile-sidebar">
                                        <X size={18} />
                                    </button>
                                ) : (
                                    <button onClick={toggle} className="sidebar-collapse-btn" data-testid="sidebar-toggle" title="Daralt">
                                        <ChevronsLeft size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                        {company?.slug && <div className="sidebar-subtitle">/{company.slug}</div>}
                    </>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <img src="/flowbil_512.png" alt="Flowbil" className="w-8 h-8 rounded-lg" />
                        <button onClick={toggle} className="sidebar-collapse-btn" data-testid="sidebar-toggle" title="Genislet">
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                )}
            </div>

            <nav className="sidebar-nav">
                {menuItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                    const isHighlight = item.highlight;

                    return (
                        <React.Fragment key={item.to}>
                            {item.section && (!collapsed || isMobile) && (
                                <div className="sidebar-section-title" style={{ marginTop: index > 0 ? '8px' : 0 }}>
                                    {item.section}
                                </div>
                            )}
                            {item.section && collapsed && !isMobile && (
                                <div className="mx-3 my-2 border-t border-gray-100" />
                            )}
                            {item.newTab ? (
                                <a
                                    href={item.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={isMobile ? closeMobile : undefined}
                                    className={`sidebar-link ${isHighlight ? 'sidebar-link-highlight' : ''} ${collapsed && !isMobile ? 'sidebar-link-collapsed' : ''}`}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    title={collapsed && !isMobile ? item.label : undefined}
                                >
                                    <Icon size={18} />
                                    {(!collapsed || isMobile) && <span>{item.label}</span>}
                                </a>
                            ) : (
                                <Link
                                    to={item.to}
                                    onClick={isMobile ? closeMobile : undefined}
                                    className={`sidebar-link ${isActive ? 'active' : ''} ${isHighlight ? 'sidebar-link-highlight' : ''} ${collapsed && !isMobile ? 'sidebar-link-collapsed' : ''}`}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    title={collapsed && !isMobile ? item.label : undefined}
                                >
                                    <Icon size={18} />
                                    {(!collapsed || isMobile) && <span>{item.label}</span>}
                                </Link>
                            )}
                        </React.Fragment>
                    );
                })}
            </nav>

            <div className="sidebar-user">
                {(!collapsed || isMobile) ? (
                    <div className="flex items-center gap-3 mb-3">
                        <div className="sidebar-avatar">
                            {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                                {user?.full_name || user?.username || 'User'}
                            </div>
                            <div className="text-xs text-white/50 capitalize">{user?.type}</div>
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center mb-3">
                        <div className="sidebar-avatar">
                            {user?.full_name?.charAt(0) || 'U'}
                        </div>
                    </div>
                )}
                <Link
                    to="/"
                    onClick={(e) => { e.preventDefault(); logout(); window.location.href = '/'; }}
                    className={`sidebar-link sidebar-link-logout ${collapsed && !isMobile ? 'sidebar-link-collapsed' : ''}`}
                    data-testid="logout-btn"
                >
                    <LogOut size={18} />
                    {(!collapsed || isMobile) && <span>Cikis Yap</span>}
                </Link>
            </div>
        </>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <aside
                className={`sidebar hidden md:flex ${collapsed ? 'sidebar-collapsed' : ''}`}
                data-testid="sidebar"
            >
                {sidebarContent(false)}
            </aside>

            {/* Mobile Sidebar Overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 md:hidden" style={{ zIndex: 10001 }} data-testid="mobile-sidebar-overlay">
                    <div className="absolute inset-0 bg-black/30" onClick={closeMobile} />
                    <aside className="sidebar-mobile" data-testid="mobile-sidebar">
                        {sidebarContent(true)}
                    </aside>
                </div>
            )}
        </>
    );
};

export default Sidebar;
